/**
 * Conversation Page Facade (100% Shared)
 * 
 * Composes all necessary facades for the conversation page.
 * Uses dependency injection pattern to remain platform-agnostic.
 * 
 * This facade is 100% shared between web and plugin.
 * Platform-specific implementations (navigation, auth) are injected.
 * 
 * Usage (Web):
 * ```tsx
 * const navigation = useWebNavigation();
 * const auth = useAuthDialog();
 * const facade = useConversationPageFacade({ navigation, auth, showToast });
 * ```
 * 
 * Usage (Plugin):
 * ```tsx
 * const navigation = usePluginNavigation();
 * const auth = usePairingCodeAuth();
 * const facade = useConversationPageFacade({ navigation, auth, showToast });
 * ```
 * 
 * @module useConversationPageFacade
 * @category Facades/Pages
 */

import { useMemo, useCallback, useState, useEffect, useRef } from 'react';
import { useAppSelector, useAppDispatch } from '../../../store/hooks';
import { selectIsAppLoading, selectCurrentProjectId, selectIsLoggedIn, setCurrentProjectId, getUserProfile, selectCurrentScopeId } from '../../../store/slices/appSlice';
import { selectConversationState, selectConversationMessages, selectConversationMode, selectConversationName, selectIsOnboardingPrototyper, setName, setMode, resetConversation, setMessages, setConversationId } from '../../../store/slices/conversationSlice';
import { updateProjectMode } from '../../../store/slices/projectSlice';
import { useHeaderConfig } from '../../useHeaderConfig';
import { useConversationFacade } from '../shared/useConversationFacade';
import { useConversationStreamingFacade } from '../shared/useConversationStreamingFacade';
import { usePrototyperFacade } from '../shared/usePrototyperFacade';
import { useConversationHeadersFacade } from '../shared/useConversationHeadersFacade';
import { useProjectFacade } from '../shared/useProjectFacade';
import { useSidebarConfig } from '../shared/useSidebarConfig';
import { useDesignInsightsFacade } from '../shared/useDesignInsightsFacade';
import { useFindingsFacade } from '../shared/useFindingsFacade';
import type { NavigationFacade, AuthFacade, ShowToastFn } from '../shared/types';
import { getBestConversationId, isValidConversationId } from '../../../utils/conversationUtils';
import { isConversationAccessDenied } from '../../../utils/httpErrors';
import type { ConversationHeaders, ForcedArtifact, Message } from '../../../types/conversation.types';
import type { ProjectMode } from '../../../types/project.types';
import type { FindingAction } from '../../../types/findings.types';
import {
  isFindingEvent,
  isFindingStatusChangedEvent,
  isResultEvent,
  isVerdictRecomputedEvent,
} from '../../../types/sse-events';
import {
  conversationIdRestoredOnLoad,
  forgetRestoredConversation,
} from './restoredConversation';

// ==================== Types ====================

export interface ConversationPageFacadeParams {
  /**
   * Platform-specific navigation implementation
   */
  navigation: NavigationFacade;

  /**
   * Platform-specific auth implementation
   */
  auth: AuthFacade;

  /**
   * Platform-specific toast notification function
   */
  showToast: ShowToastFn;

  /**
   * URL parameter for conversation ID (from routing)
   */
  urlConversationId?: string | null;

  /**
   * Callback when project creation is ready
   */
  onProjectCreationReady?: (conversationId: string) => void;

  /**
   * Callback when account creation is required
   */
  onAccountCreationRequired?: () => void;

  /**
   * Optional initial mode (e.g., from URL parameter for plugin redirects)
   */
  initialMode?: 'clarify' | 'prototyper';

  /**
   * Callback to refresh user profile (to update credit balance after prototype creation)
   */
  onRefreshUserProfile?: () => Promise<void>;

  /**
   * Optional sidebar variant — 'scope' renders Clarify/Prototyper/Files/Artifacts
   */
  sidebarPageType?: 'scope';

  /**
   * Scope workspace: overrides the URL-based conversation ID with a scope-managed one.
   */
  urlConversationIdOverride?: string | null;

  /**
   * Scope workspace: external control of the active sidebar mode and mode-switch handler.
   * When provided with sidebarPageType='scope', the sidebar uses this instead of internal mode state.
   */
  scopeModeControl?: {
    // clarify / prototyper / strategy are all real AI modes and map 1:1
    // to the mode sent to the streaming API.
    activeMode: 'clarify' | 'prototyper' | 'strategy';
    onModeChange: (mode: 'clarify' | 'prototyper' | 'strategy') => void;
    /** Conversations of activeMode, ordered most-recent-first. */
    conversations?: Array<{ id: string; title: string }>;
    /** True while the recent-chats list is loading — sidebar shows shimmer rows. */
    isLoadingConversations?: boolean;
    activeConversationId?: string | null;
    onSelectConversation?: (conversationId: string) => void;
    onCreateConversation?: () => void;
    isCreatingConversation?: boolean;
    /**
     * True when the workspace is in the explicit "new chat" route (`…/new`):
     * fire exactly one `auto_prompt` greeting. This is the *only* greeting
     * trigger — there is no empty-URL inference anymore.
     */
    isNewChat?: boolean;
    /** Where the header back arrow / browser back should go (e.g. /:slug/explore). */
    onBack?: () => void;
    /** Text shown in the header (the scope's name) instead of the per-chat title. */
    scopeTitle?: string;
    /** Items rendered in the header three-dot menu (Files / Update info / Brand Sync, etc.). */
    headerMenuItems?: any[];
    /** When provided, enables the sidebar "Files" item and opens the UploadFiles dialog. */
    onOpenFiles?: () => void;
    /** When provided, enables the sidebar "Artifacts" item and navigates to the artifacts page. */
    onOpenArtifacts?: () => void;
  };
}

export interface ConversationPageFacade {
  // ========== State ==========
  state: {
    isLoggedIn: boolean;
    isAppLoading: boolean;
    currentProjectId: string | null;
    currentScopeId: string | null;
    currentConversationId: string | null;
    messages: any[];
    isStreaming: boolean;
    isLoading: boolean;
    isLoadingDetails: boolean;
    /** True when Nest/AI returned 403/404 for this conversation (show 404 page). */
    conversationNotFound: boolean;
    isActive: boolean;
    session_id: string | null;
    conversationId: string | null;
    name: string;
    scopeTitle: string | null;
    conversationTitle: string | null;
    init_message: string;
    skip_analysis: boolean;
    initialAttachmentIds?: string[];
    llmFamily?: string;
    planDetails?: any;
    isOnboardingPrototyper?: boolean;
    initiatedFromUncover?: boolean;
  };

  // ========== Sidebar ==========
  sidebar: ReturnType<typeof useSidebarConfig>;

  /** Header three-dot menu items for scope workspace (empty for project flows). */
  scopeHeaderMenu: any[];

  // ========== Mode ==========
  mode: {
    currentMode: ProjectMode;
    handleModeChange: (mode: ProjectMode) => void;
  };

  // ========== Header ==========
  header: ReturnType<typeof useHeaderConfig>;

  // ========== Navigation ==========
  navigation: NavigationFacade;

  // ========== Conversation Management ==========
  conversation: ReturnType<typeof useConversationFacade>;

  // ========== Streaming ==========
  streaming: ReturnType<typeof useConversationStreamingFacade>;

  // ========== Prototyper ==========
  prototyper: ReturnType<typeof usePrototyperFacade>;

  // ========== Design Insights ==========
  designInsights: ReturnType<typeof useDesignInsightsFacade>;

  // ========== Scope Intelligence Findings ==========
  findings: ReturnType<typeof useFindingsFacade>;

  // ========== Headers ==========
  headers: ReturnType<typeof useConversationHeadersFacade>;

  // ========== Project ==========
  project: ReturnType<typeof useProjectFacade>;

  // ========== Auth ==========
  auth: AuthFacade;

  // ========== Actions ==========
  actions: {
    /**
     * Submit a message in the conversation
     * @param modeOverride - Optional mode override (e.g., 'prototyper' for generating prototype from uncover mode)
     * @param forcedArtifact - The `forced_artifact` enum value (Generate Prototype → 'prototype';
     *   Strategy / Experience tool → its artifact type). The backend routes deterministically
     *   from this value. Omit for ordinary context-aware routing.
     */
    submitMessage: (message: string, attachmentIds?: string[], onComplete?: (newConversationId?: string, headers?: ConversationHeaders) => void, modeOverride?: 'clarify' | 'prototyper' | 'strategy', conversationIdOverride?: string, forcedArtifact?: ForcedArtifact) => void;

    /**
     * Cancel ongoing streaming
     */
    cancelStreaming: () => void;

    /**
     * Load conversation history
     */
    loadHistory: (conversationId: string) => Promise<Message[]>;

    /**
     * Navigate to conversation
     */
    navigateToConversation: (conversationId: string) => void;

    /**
     * Send feedback (like/dislike) for a message
     */
    sendFeedback: (type: 'like' | 'dislike', messageId: string, projectId?: string | null) => Promise<void>;

    /**
     * Ensure a conversation shell exists before uploading chat attachments.
     */
    ensureConversationForAttachments: () => Promise<string | undefined>;

    /**
     * Resolve a Scope Intelligence finding; investigate auto-sends chat message.
     */
    resolveFinding: (findingId: string, action: FindingAction) => Promise<void>;

    /**
     * Refresh findings panel after attachment upload (live layer may add cards).
     */
    refreshFindingsPanel: () => Promise<void>;
  };
}

/**
 * Conversation Page Facade Hook
 *
 * Composes all necessary facades and state for the conversation page.
 * 100% shared between web and plugin through dependency injection.
 */
export function useConversationPageFacade({
  navigation,
  auth,
  showToast,
  urlConversationId,
  onProjectCreationReady,
  onAccountCreationRequired,
  initialMode,
  onRefreshUserProfile,
  sidebarPageType,
  urlConversationIdOverride,
  scopeModeControl,
}: ConversationPageFacadeParams): ConversationPageFacade {
  // ========== Global State ==========
  const dispatch = useAppDispatch();
  const isAppLoading = useAppSelector(selectIsAppLoading);
  const isLoggedIn = useAppSelector(selectIsLoggedIn);
  const currentProjectId = useAppSelector(selectCurrentProjectId);
  const currentScopeId = useAppSelector(selectCurrentScopeId);
  const conversationState = useAppSelector(selectConversationState);
  // The conversation this page load restored from sessionStorage: its history is always loaded
  // from the server (see `restoredConversation.ts`).
  const restoredConversationId = conversationIdRestoredOnLoad(conversationState.conversationId);
  const conversationName = useAppSelector(selectConversationName);
  const messages = useAppSelector(selectConversationMessages);
  const conversationMode = useAppSelector(selectConversationMode);
  const isOnboardingPrototyper = useAppSelector(selectIsOnboardingPrototyper);

  // ========== Plan Details from Redux ==========
  const planDetails = useAppSelector(state => state.app.planDetails);
  // Try to get accessToken from auth facade, fallback to empty string
  const accessToken = (auth && (auth as any).accessToken) ? (auth as any).accessToken : '';
  useEffect(() => {
    if (accessToken) {
      dispatch(getUserProfile(accessToken));
    }
  }, [accessToken, dispatch]);

  // ========== Local State ==========
  const [actualConversationId, setActualConversationId] = useState<string | null>(null);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [conversationNotFound, setConversationNotFound] = useState(false);
  // Map conversationMode to ProjectMode, with URL mode taking priority
  const [currentMode, setCurrentMode] = useState<ProjectMode>(() => {
    if (initialMode) return initialMode;
    if (conversationMode === 'prototyper') return 'prototyper';
    return 'clarify';
  });
  const hasNavigatedRef = useRef(false);
  const hasFetchedDetailsRef = useRef<Set<string>>(new Set());
  const lastLoadedConversationRef = useRef<string | null>(null);
  /** Fires the `…/new` auto-prompt greeting exactly once per new-chat mount. */
  const greetingFiredRef = useRef(false);

  // ========== Computed Values ==========
  // When scope mode is active, the override takes priority over URL-based ID
  const effectiveUrlConversationId = urlConversationIdOverride !== undefined
    ? urlConversationIdOverride
    : urlConversationId;

  // On access denied (403/404), force currentConversationId to null so all
  // downstream consumers (workbench boot, findings, prototype creation, streaming)
  // no-op against a conversation the user cannot access.
  const currentConversationId = conversationNotFound
    ? null
    : getBestConversationId(
        actualConversationId,
        effectiveUrlConversationId,
        conversationState.conversationId,
      );

  // ========== Reusable Facades ==========
  const conversation = useConversationFacade();
  const project = useProjectFacade();

  // ========== Streaming Facade ==========
  const streaming = useConversationStreamingFacade();


  // ========== Prototyper Facade ==========
  const prototyper = usePrototyperFacade(currentProjectId || '', currentConversationId);

  // ========== Design Insights Facade ==========
  const designInsights = useDesignInsightsFacade();

  // ========== Scope Intelligence Findings Facade ==========
  const findings = useFindingsFacade();

  // ========== Headers Facade ==========
  const headers = useConversationHeadersFacade({
    isLoggedIn,
    onProjectCreationReady: useCallback((conversationId: string) => {
      if (onProjectCreationReady) {
        onProjectCreationReady(conversationId);
      } else {
        // Default behavior: navigate to project creation
        showToast({
          message: 'Ready to create project!',
          severity: 'success',
        });
      }
    }, [onProjectCreationReady, showToast]),
    onAccountCreationRequired: useCallback(() => {
      if (onAccountCreationRequired) {
        onAccountCreationRequired();
      } else {
        // Default behavior: open auth dialog
        auth.openRegister?.();
      }
    }, [onAccountCreationRequired, auth]),
  });

  // ========== Header Config ==========
  const headerConfig = useHeaderConfig({
    onNavigateToProfile: navigation.toProfile,
    onNavigateToBilling: navigation.toBilling,
    onNavigateToReport: navigation.toReportProblem,
    onLoginClick: auth.openLogin,
    onRegisterClick: auth.openRegister,
    showToast,
    currentPath: '/',
  });

  // ========== Mode Management ==========
  /** Mode sent to chat / shell APIs — scope URL mode wins over stale Redux after resetConversation. */
  const resolvedApiMode = useMemo((): ProjectMode => {
    if (scopeModeControl?.activeMode) {
      // clarify / prototyper / strategy are all real AI modes — pass through.
      return scopeModeControl.activeMode;
    }
    if (currentMode) return currentMode;
    return conversationState.mode ?? 'clarify';
  }, [scopeModeControl?.activeMode, currentMode, conversationState.mode]);

  const displayMode: ProjectMode = scopeModeControl?.activeMode ?? currentMode;

  // Sync local mode state with Redux (only if no initialMode from URL)
  useEffect(() => {
    if (!initialMode && !scopeModeControl?.activeMode) {
      const mappedMode: ProjectMode = conversationMode ?? 'clarify';
      if (mappedMode !== currentMode) {
        setCurrentMode(mappedMode);
      }
    }
  }, [conversationMode, currentMode, initialMode, scopeModeControl?.activeMode]);

  // Scope workspace: keep local mode aligned with URL-driven sidebar selection.
  useEffect(() => {
    if (!scopeModeControl?.activeMode) return;
    if (currentMode !== scopeModeControl.activeMode) {
      setCurrentMode(scopeModeControl.activeMode);
    }
  }, [scopeModeControl?.activeMode, currentMode]);

  // Update conversation mode in Redux if initialMode is provided (e.g., from plugin redirect)
  useEffect(() => {
    if (initialMode && currentMode === initialMode) {
      const conversationModeValue: 'clarify' | 'prototyper' = initialMode === 'prototyper' ? 'prototyper' : 'clarify';
      dispatch(setMode(conversationModeValue as any));
    }
  }, [initialMode, currentMode, dispatch]);

  // Handle mode changes - update project mode and navigate to project details page
  const handleMenuModeChange = useCallback((newMode: ProjectMode) => {
    if (!currentProjectId) {
      console.warn('Cannot change mode: no current project ID');
      return;
    }

    // Update project mode in Redux store and API
    dispatch(updateProjectMode({ projectId: currentProjectId, mode: newMode }));
    
    // Navigate to project details page
    navigation.toProjectDetails?.(currentProjectId);
  }, [dispatch, currentProjectId, navigation]);

  // Handle mode changes - update both local state and Redux
  // For tmp conversations: only allow prototyper if accessed from onboarding
  const handleModeChange = useCallback((newMode: ProjectMode) => {
    // For tmp conversations (no project), validate prototyper access
    if (!currentProjectId && newMode === 'prototyper' && !isOnboardingPrototyper) {
      console.warn('Cannot switch to prototyper mode: not accessible from onboarding');
      showToast({
        message: 'Prototyper mode requires a project. Please create a project or access prototyper from onboarding.',
        severity: 'warning',
      });
      return;
    }

    setCurrentMode(newMode);
    // ProjectMode and ConversationStepMode share the same string values
    // (clarify / prototyper / strategy) — pass through directly.
    dispatch(setMode(newMode as any));
  }, [dispatch, currentProjectId, isOnboardingPrototyper, showToast]);


  // ========== Sidebar Config ==========
  const sidebar = useSidebarConfig({
    pageType: sidebarPageType ?? 'conversation',
    isLoggedIn,
    onResetConversation: useCallback(() => {
      // Reset conversation state and navigate to new conversation
      dispatch(resetConversation());
      navigation.toConversation?.();
    }, [dispatch, navigation]),
    onNavigateToLanding: useCallback(() => {
      navigation.toLanding?.();
    }, [navigation]),
    // When scope mode control is provided, use external mode; otherwise use internal mode
    currentMode: scopeModeControl
      ? scopeModeControl.activeMode
      : conversationState.initiatedFromUncover ? 'clarify' : currentMode,
    // When scope mode control is provided, scope page handles conversation switching
    onModeChange: scopeModeControl
      ? (mode) => scopeModeControl.onModeChange(mode)
      : currentProjectId ? handleMenuModeChange : handleModeChange,
    onHomeClick: () => {
      navigation.toLanding?.();
    },
    hasProject: !!currentProjectId,
    isOnboardingPrototyper,
    scopeConversations: scopeModeControl?.conversations,
    isLoadingScopeConversations: scopeModeControl?.isLoadingConversations,
    scopeActiveConversationId: scopeModeControl?.activeConversationId,
    onSelectScopeConversation: scopeModeControl?.onSelectConversation,
    onCreateScopeConversation: scopeModeControl?.onCreateConversation,
    isCreatingScopeConversation: scopeModeControl?.isCreatingConversation,
    onOpenScopeFiles: scopeModeControl?.onOpenFiles,
    onOpenScopeArtifacts: scopeModeControl?.onOpenArtifacts,
  });

  // ========== Effects ==========
  
  /**
   * Navigate to conversation when ID changes (after streaming creates new conversation)
   * Only navigate if:
   * 1. We have a conversation ID from Redux
   * 2. It's different from the URL conversation ID (new conversation created)
   * 3. We haven't navigated yet for this ID
   */
  useEffect(() => {
    if (
      conversationState.conversationId &&
      conversationState.conversationId !== effectiveUrlConversationId &&
      !hasNavigatedRef.current
    ) {
      hasNavigatedRef.current = true;

      // Mark this as already loaded since we just created it (messages are
      // already in Redux). Without this, the URL→fetchDetails effect later
      // re-fetches the same conversation we just streamed, replacing the
      // visible messages with the API response (the "refresh" the user sees).
      lastLoadedConversationRef.current = conversationState.conversationId;
      hasFetchedDetailsRef.current.add(conversationState.conversationId);

      // Scope workspace owns its own URL (/:slug/:scopeId?chat=…) and writes
      // the chat param itself via ScopeConversationPage's sync effect — don't
      // redirect to /conversation/:id here.
      if (sidebarPageType !== 'scope') {
        navigation.toConversation?.(conversationState.conversationId);
      }
    }
  }, [conversationState.conversationId, effectiveUrlConversationId, navigation, sidebarPageType]);

  /**
   * Clear onboarding prototyper flag when project is created
   * Once user has a project, they no longer need special onboarding access
   */
  useEffect(() => {
    if (currentProjectId && isOnboardingPrototyper) {
      conversation.setOnboardingPrototyper(false);
    }
  }, [currentProjectId, isOnboardingPrototyper, conversation]);

  /**
   * Fire the proactive auto-prompt greeting — the *single*, intent-driven
   * trigger. It runs only on the explicit new-chat route (`…/new`, surfaced as
   * `scopeModeControl.isNewChat`), never inferred from an empty URL.
   *
   * The greeting text comes from the backend (the `auto_prompt` flag). The FE
   * only triggers it: `createStreamingConversation` with `auto_prompt: true` and
   * no `user_message`, so the streamed reply renders as the opening message with
   * no preceding user bubble. The backend branches on scope history to pick the
   * first-conversation vs. continuation greeting — the FE call is identical for
   * both. Once the conversation is created, the workspace replaces the URL from
   * `…/new` to `…/:createdId`, so `isNewChat` flips false and this cannot re-run.
   */
  const isScopeMode = Boolean(scopeModeControl);
  const isNewChat = Boolean(scopeModeControl?.isNewChat);
  const { createStreamingConversation } = streaming;

  // Re-arm the one-shot greeting whenever we leave the new-chat route, so a
  // subsequent "New" fires its own greeting.
  useEffect(() => {
    if (!isNewChat) greetingFiredRef.current = false;
  }, [isNewChat]);

  useEffect(() => {
    if (!isScopeMode || !isNewChat) return;
    if (!currentScopeId) return;
    if (conversationState.conversationId) return; // already created / streaming
    if (greetingFiredRef.current) return; // strict-mode double-mount guard
    greetingFiredRef.current = true;

    createStreamingConversation(
      '', // no user_message — backend owns the priming prompt
      undefined, // existingConversationId (creates a new conversation)
      undefined, // onComplete
      undefined, // setIsStreaming (handled internally)
      undefined, // attachmentIds
      false, // skipAnalysis
      undefined, // isHiddenUserQuery — the backend marks the persisted turn hidden
      undefined, // actionType
      conversationState.llmFamily,
      resolvedApiMode,
      true, // autoPrompt
    ).catch((error) => {
      // Degrade gracefully to a usable empty composer; allow a retry.
      console.error('Auto-prompt greeting failed:', error);
      greetingFiredRef.current = false;
    });
  }, [
    isScopeMode,
    isNewChat,
    currentScopeId,
    conversationState.conversationId,
    conversationState.llmFamily,
    resolvedApiMode,
    createStreamingConversation,
  ]);

  /**
   * Fetch conversation details when URL conversation ID changes
   * This handles:
   * 1. Initial load of a conversation
   * 2. Switching between different conversations
   * 3. Clearing old messages and fetching new ones
   */
  useEffect(() => {
    const getDetailsFromState = (id: string) => {
      // Check if the current conversation state matches the requested ID
      // Either by conversationId (after streaming completes) OR by being the active conversation
      const isCurrentConversation = conversationState.conversationId === id || 
                                     (conversationState.isActive && !conversationState.conversationId);
      
      if (isCurrentConversation) {
        // Use the name from state, or init_message as fallback
        const title = conversationName || conversationState.init_message;
        
        if (title) {
          return {
            id: id,
            title: title,
            project_id: currentProjectId || '',
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          };
        }
      }
      return null;
    };

    const fetchDetails = async () => {
      // Skip if no URL conversation ID
      if (!effectiveUrlConversationId || !isValidConversationId(effectiveUrlConversationId)) {
        setConversationNotFound(false);
        return;
      }

      // The URL points at the conversation currently held in Redux — i.e. the
      // one we just created and are streaming into (its id is set on create,
      // not on load). Its messages are already live in state, so refetching its
      // history (which the DB write-back hasn't populated yet) would return
      // empty and wipe the in-flight stream. This makes every new chat safe, not
      // just the first (the old `hasNavigatedRef` one-shot guard missed the 2nd+).
      if (
        effectiveUrlConversationId === conversationState.conversationId &&
        // Not the one restored from sessionStorage on reload: that one was not created here, and
        // skipping it showed the tab's saved messages instead of the server's history.
        effectiveUrlConversationId !== restoredConversationId
      ) {
        lastLoadedConversationRef.current = effectiveUrlConversationId;
        hasFetchedDetailsRef.current.add(effectiveUrlConversationId);
        setConversationNotFound(false);
        return;
      }

      // Check if we're switching to a different conversation
      const isDifferentConversation = lastLoadedConversationRef.current !== effectiveUrlConversationId;

      if (isDifferentConversation) {
        // Clear messages and reset state when switching conversations
        hasFetchedDetailsRef.current.clear();
        lastLoadedConversationRef.current = effectiveUrlConversationId;
        setConversationNotFound(false);
        // Drop the outgoing conversation's messages immediately so the incoming
        // one never flashes the previous chat's cached content — it starts blank
        // and loads fresh from the API below.
        //
        // We intentionally do NOT cancel the in-flight stream on switch:
        // `cancelStreaming()` aborts the request AND sets `turnFinalizedRef`,
        // which stops a just-created conversation's turn from being written back
        // — leaving that chat empty on switch-back and blank on reload. Letting
        // the stream finish in the background persists the turn so `loadHistory`
        // can return it. (The just-created conversation you're still viewing is
        // already protected from a premature refetch by the
        // `effectiveUrlConversationId === conversationState.conversationId`
        // early-return above.)
        dispatch(setMessages([]));
        // Point Redux's conversationId at the conversation we're switching to.
        // `setConversationId` is otherwise only dispatched on create/stream, so
        // it stays pinned to the last-created conversation — which made the
        // early-return guard above mis-fire when you navigated *back* to that
        // created conversation (URL === stale conversationId ⇒ no refetch, so it
        // kept showing the previously-viewed chat's messages, with no API call).
        // Keeping conversationId in sync with the loaded conversation preserves
        // that guard's invariant.
        dispatch(setConversationId(effectiveUrlConversationId));
      }

      // Skip if we've already fetched this conversation, or if a load for the
      // SAME conversation is already in flight. A load in flight for a
      // *different* conversation must NOT block us: when you click B while A's
      // history is still pending, we have to fetch B (the `isDifferentConversation`
      // block above already cleared the fetched-set and repointed state to B).
      // The stale A response is discarded by `loadHistory`'s latest-request
      // guard, so it can't overwrite B.
      if (
        (!isDifferentConversation && isLoadingDetails) ||
        hasFetchedDetailsRef.current.has(effectiveUrlConversationId)
      ) {
        return;
      }

      hasFetchedDetailsRef.current.add(effectiveUrlConversationId);
      if (effectiveUrlConversationId === restoredConversationId) forgetRestoredConversation();

      const handleAccessDenied = (error: unknown) => {
        console.error('Conversation access denied:', error);
        setConversationNotFound(true);
        // Clear project id so needsImmediateWorkbenchBoot (which checks
        // currentProjectId) also stays false on an inaccessible conversation.
        dispatch(setCurrentProjectId(null));
      };

      // First, try to get details from current state
      const stateDetails = getDetailsFromState(effectiveUrlConversationId);
      
      if (stateDetails) {
        // We have the conversation in state and it's the same conversation, use that

        if (stateDetails.title && !conversationName) {
          dispatch(setName(stateDetails.title));
        }

        if (stateDetails.project_id) {
          dispatch(setCurrentProjectId(stateDetails.project_id));
        }

        // Do NOT early-return here just because messages already exist. The
        // just-streamed conversation (whose DB write-back is still pending, so a
        // reload would fetch `[]` and blank the chat) is already protected: the
        // navigation effect above calls `hasFetchedDetailsRef.add(conversationId)`,
        // so `fetchDetails` returns at the `has(...)` check before reaching this
        // branch. Reaching here therefore always means a *real* load — switching
        // chats or a deep link — where we MUST load history. A previous guard
        // (`if (messages.length > 0) return`) kept the prior chat's messages
        // visible under the new selection on every switch (the "blink + stale
        // content" bug); it's intentionally removed.

        // Load history after details are set. Flag loading so the conversation
        // shows the skeleton/shimmer while switching.
        setIsLoadingDetails(true);
        try {
          const loadedMessages = await streaming.loadHistory(effectiveUrlConversationId, currentProjectId);

          // A newer switch superseded this load while it was in flight — don't
          // apply its header state to the conversation now on screen.
          if (lastLoadedConversationRef.current !== effectiveUrlConversationId) return;
          // Process headers from last message after loading history
          headers.processHeadersFromLastMessage(loadedMessages, effectiveUrlConversationId);
        } catch (historyError) {
          console.error('Failed to load history:', historyError);
          if (isConversationAccessDenied(historyError)) {
            handleAccessDenied(historyError);
          } else if (sidebarPageType !== 'scope') {
            // Scope workspace stays on its own URL — never bail out to landing.
            navigation.toLanding?.();
          }
        } finally {
          setIsLoadingDetails(false);
        }
      } else {
        // State doesn't have the conversation OR we switched conversations, fetch from API
        console.log('Fetching conversation details from API for ID:', effectiveUrlConversationId);
        setIsLoadingDetails(true);

        try {
          const details = await streaming.fetchConversationDetails(effectiveUrlConversationId);

          // Update Redux with conversation details
          if (details.title) {
            dispatch(setName(details.title));
          }

          if (details.project_id) {
            dispatch(setCurrentProjectId(details.project_id));
          }

          // Load history after details are fetched
          const loadedMessages = await streaming.loadHistory(effectiveUrlConversationId, currentProjectId);

          // A newer switch superseded this load while it was in flight — don't
          // apply its header state to the conversation now on screen.
          if (lastLoadedConversationRef.current !== effectiveUrlConversationId) return;
          // Process headers from last message after loading history
          headers.processHeadersFromLastMessage(loadedMessages, effectiveUrlConversationId);
        } catch (error) {
          console.error('Failed to fetch conversation details:', error);
          // Ownership / missing conversation: do not fall through to AI history.
          if (isConversationAccessDenied(error)) {
            handleAccessDenied(error);
            return;
          }
          // Other Nest errors: still try AI history (legacy fallthrough).
          try {
            const loadedMessages = await streaming.loadHistory(effectiveUrlConversationId, currentProjectId);
            // A newer switch superseded this load while it was in flight.
            if (lastLoadedConversationRef.current !== effectiveUrlConversationId) return;
            if(loadedMessages.length === 0) {
              // Empty conversation: legitimate for a fresh scope chat. Only bail to landing outside scope mode.
              if (sidebarPageType !== 'scope') navigation.toLanding?.();
              return;
            }
            // Process headers from last message after loading history
            headers.processHeadersFromLastMessage(loadedMessages, effectiveUrlConversationId);
          } catch (historyError) {
            console.error('Failed to load history:', historyError);
            if (isConversationAccessDenied(historyError)) {
              handleAccessDenied(historyError);
            } else if (sidebarPageType !== 'scope') {
              navigation.toLanding?.();
            }
          }
        } finally {
          setIsLoadingDetails(false);
        }
      }
    };

    fetchDetails();
  }, [
    effectiveUrlConversationId, // Main trigger - when URL conversation ID changes
    isLoadingDetails,
    streaming,
    dispatch,
    currentProjectId,
    conversationState.conversationId,
    conversationName,
    conversationState.init_message,
    conversationState.isActive,
    navigation,
    headers,
    messages,
  ]);

  // ========== Scope Intelligence: load panel when scope is set ==========
  // Guard against reloading the same scope: `currentScopeId` can be dispatched
  // more than once during a load (and Strict Mode double-invokes effects in
  // dev), which otherwise fires the findings request twice.
  const loadedFindingsScopeRef = useRef<string | null>(null);
  useEffect(() => {
    if (!currentScopeId) {
      loadedFindingsScopeRef.current = null;
      findings.clearPanel();
      return;
    }
    if (loadedFindingsScopeRef.current === currentScopeId) return;
    loadedFindingsScopeRef.current = currentScopeId;
    findings.loadPanel(currentScopeId);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load once per scope
  }, [currentScopeId]);

  // Keep latest findings handlers for the stream listener without re-registering every render
  const findingsHandlersRef = useRef(findings);
  findingsHandlersRef.current = findings;

  // ========== Scope Intelligence: SSE finding / lifecycle / result events ==========
  useEffect(() => {
    streaming.setStreamEventListener((event) => {
      const f = findingsHandlersRef.current;
      if (isFindingEvent(event)) {
        f.pushInTurnFinding(event);
        return;
      }
      if (isFindingStatusChangedEvent(event)) {
        f.applyFindingStatusChanged(event);
        return;
      }
      if (isVerdictRecomputedEvent(event)) {
        f.applyVerdictRecomputed(event);
        return;
      }
      if (isResultEvent(event)) {
        f.clearInTurnFlash();
        void f.refreshPanel();
      }
    });
    return () => {
      streaming.setStreamEventListener(null);
    };
  }, [streaming]);

  // ========== Actions ==========
  const actions = useMemo(() => ({
    submitMessage: (message: string, attachmentIds?: string[], onComplete?: (newConversationId?: string, headers?: ConversationHeaders) => void, modeOverride?: 'clarify' | 'prototyper' | 'strategy', conversationIdOverride?: string, forcedArtifact?: ForcedArtifact) => {
      streaming.submitMessage(message, attachmentIds);

      const activeConversationId = conversationIdOverride || currentConversationId;

      if (activeConversationId || conversationState.session_id) {
        streaming.createStreamingConversation(
          message,
          activeConversationId || undefined,
          onComplete,
          undefined, // setIsStreaming handled internally
          attachmentIds,
          false, // skip_analysis: false
          undefined, // isHiddenUserQuery
          undefined, // actionType
          conversationState.llmFamily, // Use llm_family from state
          modeOverride ?? resolvedApiMode,
          undefined, // autoPrompt
          undefined, // answerBody
          forcedArtifact
        );
      }
    },

    cancelStreaming: () => {
      streaming.cancelStreaming();
    },

    loadHistory: async (conversationId: string) => {
      return await streaming.loadHistory(conversationId, currentProjectId);
    },


    navigateToConversation: (conversationId: string) => {
      navigation.toConversation?.(conversationId);
    },

    sendFeedback: async (type: 'like' | 'dislike', messageId: string, projectId?: string | null) => {
      try {
        const isLiked = type === 'like';
        await streaming.sendFeedback({
          messageID: messageId,
          isLiked,
          projectId: projectId || undefined,
        });
      } catch (error) {
        console.error('Failed to send feedback:', error);
        showToast({
          message: 'Failed to send feedback. Please try again.',
          severity: 'error',
        });
        throw error;
      }
    },

    ensureConversationForAttachments: () => streaming.ensureConversationShell(resolvedApiMode),

    resolveFinding: async (findingId: string, action: FindingAction) => {
      const response = await findings.resolve(findingId, action);
      if (!response) {
        showToast({
          message: 'Failed to resolve finding. Please try again.',
          severity: 'error',
        });
        return;
      }

      if (action === 'investigate' && response.investigate_message) {
        // Never show raw finding IDs in chat — prefer the human-readable summary.
        const finding = findings.findings.find((f) => f.id === findingId);
        const summary = finding?.content_summary?.trim();
        const hasReadinessAnalysis =
          finding?.finding_type === 'scope_readiness' && Boolean(finding.details?.analysis);
        const msg = hasReadinessAnalysis
          ? response.investigate_message
          : summary
          ? `Let's dig into: ${summary}`
          : response.investigate_message
              .replace(
                /\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi,
                ''
              )
              .replace(/finding\s+/gi, '')
              .replace(/\s{2,}/g, ' ')
              .replace(/\s+:/g, ':')
              .trim();

        streaming.submitMessage(msg);
        if (currentConversationId || conversationState.session_id) {
          streaming.createStreamingConversation(
            msg,
            currentConversationId || undefined,
            undefined,
            undefined,
            undefined,
            false,
            undefined,
            undefined,
            conversationState.llmFamily,
            resolvedApiMode
          );
        }
      } else {
        // Prefer authoritative panel after non-investigate resolves
        void findings.refreshPanel();
      }
    },

    refreshFindingsPanel: () => findings.refreshPanel(),
  }), [
    streaming,
    prototyper,
    navigation,
    currentConversationId,
    currentProjectId,
    currentMode,
    resolvedApiMode,
    conversationState.session_id,
    conversationState.llmFamily,
    showToast,
    findings,
    scopeModeControl,
  ]);

  // ========== Return Composed Facade ==========
  return useMemo(
    () => ({
      state: {
        isLoggedIn,
        isAppLoading,
        currentProjectId,
        currentScopeId,
        currentConversationId,
        messages,
        isStreaming: streaming.isStreaming, // Get from streaming facade
        isLoading: conversationState.isLoading,
        isLoadingDetails,
        conversationNotFound,
        isActive: conversationState.isActive,
        session_id: conversationState.session_id,
        conversationId: conversationState.conversationId || null,
        // In scope mode the header shows the scope name, not the per-chat title.
        name: scopeModeControl?.scopeTitle ?? conversationState.name,
        // Surfaced separately so the header can render "scope name / conversation title" breadcrumbs.
        scopeTitle: scopeModeControl?.scopeTitle ?? null,
        conversationTitle: conversationState.name || null,
        init_message: conversationState.init_message,
        skip_analysis: conversationState.skip_analysis || false,
        initialAttachmentIds: conversationState.initialAttachmentIds,
        llmFamily: conversationState.llmFamily,
        planDetails,
        isOnboardingPrototyper,
        initiatedFromUncover: conversationState.initiatedFromUncover,
      },
      sidebar,
      // Header three-dot menu items provided by the scope page (Files / Update info / Brand Sync).
      // Empty (or undefined) for non-scope flows so the existing project flow stays unchanged.
      scopeHeaderMenu: scopeModeControl?.headerMenuItems ?? [],
      mode: {
        currentMode: displayMode,
        handleModeChange,
        handleMenuModeChange
      },
      header: headerConfig,
      navigation,
      conversation,
      streaming,
      prototyper,
      designInsights,
      findings,
      headers,
      project,
      auth,
      actions,
    }),
    [
      isLoggedIn,
      isAppLoading,
      currentProjectId,
      currentScopeId,
      currentConversationId,
      messages,
      isLoadingDetails,
      conversationNotFound,
      conversationState,
      sidebar,
      displayMode,
      handleModeChange,
      handleMenuModeChange,
      headerConfig,
      navigation,
      conversation,
      streaming,
      prototyper,
      designInsights,
      findings,
      headers,
      project,
      auth,
      actions,
    ]
  );
}
